import {
    Alert,
    Modal,
    styled,
    Typography,
    IconButton,
    Box,
    CircularProgress,
    FormControl,
    FormLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormGroup,
    Checkbox,
} from "@mui/material";
import { useEffect, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ReactSelect from "react-select";
import { actions } from "../../../../redux/store/store";
import { useGetMetrosQuery } from "../../../../api/metro";
import ConfirmDialog from "../confirmDialog";
import { useBulkUpdateDriverMetroMutation, useRequestBackgroundCheckMutation } from "../../../../api/drivers";
import { useDocumentApprovalMutation } from "../../../../api/rejectedDocumentReason";
import { checkForError } from "../../../../constants/extras/errorHandlers";
import { sendSuccessMessage } from "../../../../constants/extras/sendSuccessMsg";
import { useGetReasonsQuery } from "../../../../api/reason";
import { driveDocumentType } from "../../../../constants/menuLists";
import { useSelector } from "react-redux";
import { checkPermission } from "../../../../auth/permissions";

const MuiAccordion = styled((props) => (
    <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:before": {
        display: "none",
    },
}));

const MuiAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const MuiAccordionSummary = styled((props) => (
    <AccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === "dark" ? "rgba(255, 255, 255, .05)" : "#ccd7dd",

    "& .MuiAccordionSummary-content": {
        marginLeft: theme.spacing(1),
    },
}));

const styles = (width) => {
    return {
        placeholder: (base) => ({
            ...base,
            fontSize: "0.9rem",
            fontWeight: "normal",
        }),
        control: (styles) => ({
            ...styles,
            borderRadius: "2px",
            boxShadow: "none",
            width: "18rem",
            height: "2.8rem",
            "&:hover": {
                borderColor: "#e3eaf3",
            },
            borderColor: "#e3eaf3",
            fontSize: "1rem",
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: "190px",
            paddingTop: 0,
            paddingBottom: 0,
        }),
    };
};

export const DriverBulkUpdate = (props) => {
    const role = useSelector((state) => state.auth.currentUser?.role);
    const { drivers } = props?.id || {};
    const { selectedVisibleDrivers } = drivers;
    const selectedDriverIds = selectedVisibleDrivers.map(
        (driver) => driver._id,
    );
    const statuses = selectedVisibleDrivers.map((driver) => driver.status);
    const isAllStatusRejected = statuses.every(
        (status) => status.toLowerCase() === "rejected",
    );
    const allHaveConsent = selectedVisibleDrivers.every(
        (driver) => driver?.consents?.consentId,
    );

    const [expanded, setExpanded] = useState(false);
    const [selectedMetro, setSelectedMetro] = useState("");
    const [showMetroConfirmDialog, setShowMetroConfirmDialog] = useState(false);
    const [showStatusConfirmDialog, setShowStatusConfirmDialog] =
        useState(false);
    const [showBackgroundCheckConfirmDialog, setShowBackgroundCheckConfirmDialog] = useState(false);

    const [reasonData, setReasonData] = useState({
        driversLicense: [],
        insurancePolicy: [],
        selfie: [],
        signature: []
      });
    const [selectedReasonType, setSelectedReasonType] = useState("");
    const [selectedReasons, setSelectedReasons] = useState([]);

    const handleChange = (panel) => (_, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const onCancel = () => {
        actions.modal.closeDriverBulkUpdate();
    };

    const { data: metros } = useGetMetrosQuery(null, {
        refetchOnMountOrArgChange: true,
    });

    const [bulkUpdateMetro, { isLoading: isMetroUpdateLoading }] =
        useBulkUpdateDriverMetroMutation();

    const [documentApprovalFn, { isLoading: isStatusUpdateLoading }] =
        useDocumentApprovalMutation();

    const [backgroundCheckFn, { isLoading: isBackgroundCheckLoading }] =
        useRequestBackgroundCheckMutation();

    const { data: reasonApiData } = useGetReasonsQuery([driveDocumentType.selfie, driveDocumentType.driversLicense, driveDocumentType.insurancePolicy, driveDocumentType.signature], {
        refetchOnMountOrArgChange: true
    });

    useEffect(() => {
        if (reasonApiData?.length) {
            const driversLicense = reasonApiData.filter((item) => item.type === driveDocumentType.driversLicense);
            const insurancePolicy = reasonApiData.filter((item) => item.type === driveDocumentType.insurancePolicy);
            const selfie = reasonApiData.filter((item) => item.type === driveDocumentType.selfie);
            const signature = reasonApiData.filter((item) => item.type === driveDocumentType.signature);

            setReasonData({
                driversLicense,
                insurancePolicy,
                selfie,
                signature
            });
        }
    }, [reasonApiData]);

    const handleMetroUpdate = async () => {
        setShowMetroConfirmDialog(false);
        const result = await bulkUpdateMetro({
            metro: selectedMetro.value,
            driverIds: selectedDriverIds,
        }).unwrap();
        if (result?.error) {
            return checkForError(result);
        }

        onCancel();
        actions.notifications.add({ message: result.message, status: "success" });
        actions.refetch.setIsDataRefetch(true);
    };

    const handleStatusUpdate = async () => {
        setShowStatusConfirmDialog(false);

        for (const driverId of selectedDriverIds) {
            const payload = {
                driver: driverId,
                docApproval: [
                    {
                        type: selectedReasonType,
                        reasons: selectedReasons,
                    },
                ],
            };
            const result = await documentApprovalFn(payload);
            if (result?.error) {
                return checkForError(result);
            }
        }
        onCancel();
        sendSuccessMessage("Driver Review saved successfully...");
        actions.refetch.setIsDataRefetch(true);
    };

    const handleReasonTypeSelect = (event) => {
        setSelectedReasons([]);
        setSelectedReasonType(event.target.value);
    };

    const handleReasonSelect = (event) => {
        const { value, checked } = event.target;

        setSelectedReasons((prev) =>
            checked
                ? [...prev, value]
                : prev.filter((id) => id !== value)
        );
    };

    const handleBackgroundCheck = async () => {
        setShowBackgroundCheckConfirmDialog(false);

        // Filter drivers that don't already have a background check initiated, completed, or processing
        const driversNeedingCheck = selectedVisibleDrivers.filter(
            (driver) => !driver?.checkrBackgroundStatus ||
                        (driver?.checkrBackgroundStatus?.toLowerCase() !== "processing" &&
                         driver?.checkrBackgroundStatus?.toLowerCase() !== "pending")
        );
        const skippedCount = selectedVisibleDrivers.length - driversNeedingCheck.length;

        if (driversNeedingCheck.length === 0) {
            sendSuccessMessage(`Background checks already initiated/completed for all selected drivers`);
            onCancel();
            return;
        }

        let successCount = 0;
        let errorCount = 0;

        for (const driver of driversNeedingCheck) {
            const result = await backgroundCheckFn(driver._id);
            if (result?.error) {
                errorCount++;
                checkForError(result);
            } else {
                successCount++;
            }
        }

        onCancel();
        let message = '';
        if (successCount > 0) {
            message += `Background check initiated for ${successCount} driver(s)`;
        }
        if (skippedCount > 0) {
            message += `${successCount > 0 ? '. ' : ''}Skipped ${skippedCount} driver(s) with background check already in progress`;
        }
        if (message) {
            sendSuccessMessage(message);
        }
        if (errorCount > 0) {
            checkForError({ message: `Failed to initiate background check for ${errorCount} driver(s)` });
        }
        actions.refetch.setIsDataRefetch(true);
    };

    return (
        <Modal
            open
            onClose={onCancel}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <div className={`modal after-loading`} style={{ width: "40rem" }}>
                <div
                    className="modal__header"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <h2 className="modal__title">
                        Bulk Driver Update ({selectedVisibleDrivers?.length})
                    </h2>

                    <IconButton
                        aria-label="close"
                        onClick={onCancel}
                        size="small"
                        sx={{
                            color: "text.secondary",

                            "&:hover": {
                                bgcolor: "action.hover",
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
                <div className="modal__body">
                    {isMetroUpdateLoading || isStatusUpdateLoading || isBackgroundCheckLoading ? (
                        <CircularProgress sx={{display: "flex", alignContent: "center", justifySelf: "center"}} />
                    ) : (
                        <>
                            {/* {Update Metro} */}
                            {checkPermission(role, "drivers.fields") && <MuiAccordion
                                className="mb-4"
                                expanded={expanded === "panel1"}
                                onChange={handleChange("panel1")}
                            >
                                <MuiAccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel1-content"
                                    id="panel1-header"
                                    className="panel1-header"
                                >
                                    <Typography>Update Metro</Typography>
                                </MuiAccordionSummary>
                                <MuiAccordionDetails>
                                    <div>
                                        <div className="mb-3">
                                            <Typography fontWeight={500} mb={2}>
                                                Update metro for selected
                                                drivers
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 4,
                                                }}
                                            >
                                                <ReactSelect
                                                    options={metros?.map(
                                                        (item) => {
                                                            return {
                                                                value: item?.name,
                                                                label: item?.name,
                                                            };
                                                        },
                                                    )}
                                                    isClearable={true}
                                                    onChange={(item) => {
                                                        setSelectedMetro(item);
                                                    }}
                                                    placeholder="Select a metro*"
                                                    value={selectedMetro}
                                                    styles={styles()}
                                                />

                                                <button
                                                    className="btn btn_accent"
                                                    style={{ fontSize: "14px" }}
                                                    disabled={!selectedMetro}
                                                    onClick={() =>
                                                        setShowMetroConfirmDialog(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    Update
                                                </button>
                                            </Box>
                                        </div>
                                    </div>
                                </MuiAccordionDetails>
                            </MuiAccordion>}

                            {/* {Update Rejected Status} */}
                            {checkPermission(role, "drivers.bulk_status") && !isAllStatusRejected && (
                                <Alert severity="warning">
                                    This action is only available when all
                                    selected drivers are in Rejected status.
                                </Alert>
                            )}
                            {checkPermission(role, "drivers.bulk_status") && <MuiAccordion
                                className="mb-4"
                                disabled={!isAllStatusRejected}
                                expanded={expanded === "panel2"}
                                onChange={handleChange("panel2")}
                            >
                                <MuiAccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel2-content"
                                    id="panel2-header"
                                    className="panel2-header"
                                >
                                    <Typography>Move status from Rejected to Needs Resubmission</Typography>
                                </MuiAccordionSummary>
                                <MuiAccordionDetails>
                                    <div>
                                        <div className="mb-0">

                                            <Typography 
                                                sx={{fontWeight: "bold", mb: 0.5}}
                                            >
                                                Select Reason Type
                                            </Typography>

                                            <FormControl>
                                                <RadioGroup
                                                    row
                                                    aria-labelledby="Document types"
                                                    name="document-types"
                                                    value={selectedReasonType}
                                                    onChange={(event) => handleReasonTypeSelect(event)}
                                                    sx={{
                                                        mb: 1
                                                    }}
                                                >
                                                    <FormControlLabel value="signature" control={<Radio />} label="Signature" />
                                                    <FormControlLabel value="driversLicense" control={<Radio />} label="Driver License" />
                                                    <FormControlLabel value="insurancePolicy" control={<Radio />} label="Vehicle Insurance" />
                                                    <FormControlLabel value="selfie" control={<Radio />} label="Selfie" />
                                                </RadioGroup>
                                            </FormControl>

                                            {selectedReasonType && (
                                                <div className="mb-4">
                                                    <Typography
                                                        sx={{ fontWeight: "bold", mb: 0.5 }}
                                                    >
                                                        Select Reasons
                                                    </Typography>

                                                    <FormGroup>
                                                        {reasonData[selectedReasonType]?.map((reason) => (
                                                            <FormControlLabel
                                                                key={reason._id}
                                                                control={
                                                                    <Checkbox
                                                                        value={reason._id}
                                                                        checked={selectedReasons.includes(reason._id)}
                                                                        onChange={handleReasonSelect}
                                                                    />
                                                                }
                                                                label={reason.reason}
                                                                sx={{
                                                                    mb: 0,
                                                                }}
                                                            />
                                                        ))}
                                                    </FormGroup>
                                                </div>
                                            )}

                                            <button
                                                className="btn btn_accent"
                                                style={{ fontSize: "14px" }}
                                                onClick={() =>
                                                    setShowStatusConfirmDialog(
                                                        true,
                                                    )
                                                }
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </MuiAccordionDetails>
                            </MuiAccordion>}

                            {/* {Request Background Check} */}
                            {checkPermission(role, "drivers.fields") && !allHaveConsent && (
                                <Alert severity="warning">
                                    This action is only available when all selected drivers have "Consent Given" status.
                                </Alert>
                            )}
                            {checkPermission(role, "drivers.fields") && <MuiAccordion
                                className="mb-4"
                                disabled={!allHaveConsent}
                                expanded={expanded === "panel3"}
                                onChange={handleChange("panel3")}
                            >
                                <MuiAccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel3-content"
                                    id="panel3-header"
                                    className="panel3-header"
                                >
                                    <Typography>Request Background Check</Typography>
                                </MuiAccordionSummary>
                                <MuiAccordionDetails>
                                    <div>
                                        <div className="mb-3">
                                            <Typography fontWeight={500} mb={2}>
                                                Initiate background checks for selected drivers
                                            </Typography>

                                            <button
                                                className="btn btn_accent"
                                                style={{ fontSize: "14px" }}
                                                onClick={() =>
                                                    setShowBackgroundCheckConfirmDialog(
                                                        true,
                                                    )
                                                }
                                            >
                                                Request Background Check
                                            </button>
                                        </div>
                                    </div>
                                </MuiAccordionDetails>
                            </MuiAccordion>}
                        </>
                    )}
                </div>

                {/* {Confirmation Modals} */}
                {showMetroConfirmDialog && (
                    <ConfirmDialog
                        title="Confirmation"
                        bodyText={`Are you sure you want to update the metro for selected drivers`}
                        isOpen
                        onClose={() => {
                            setShowMetroConfirmDialog(false);
                        }}
                        confirmAction={handleMetroUpdate}
                    />
                )}

                {showStatusConfirmDialog && (
                    <ConfirmDialog
                        title="Confirmation"
                        bodyText={`Are you sure you want to update the status for selected drivers from Rejected to Needs Resubmission`}
                        isOpen
                        onClose={() => {
                            setShowStatusConfirmDialog(false);
                        }}
                        confirmAction={handleStatusUpdate}
                    />
                )}

                {showBackgroundCheckConfirmDialog && (
                    <ConfirmDialog
                        title="Confirmation"
                        bodyText={`Are you sure you want to request background checks for ${selectedVisibleDrivers?.length} selected driver(s)?`}
                        isOpen
                        onClose={() => {
                            setShowBackgroundCheckConfirmDialog(false);
                        }}
                        confirmAction={handleBackgroundCheck}
                    />
                )}
            </div>
        </Modal>
    );
};
